import {
  Dispatch,
  FocusEvent,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import _ from "lodash";
import type { MapLayerMouseEvent } from "mapbox-gl";
import { MapRef, Marker } from "react-map-gl/mapbox-legacy";
import { confirmValidCoordinates } from "common/utils/coordinates";
import {
  isCustomMapGeometryResult,
  SearchResultProps,
} from "common-client/utils/search";
import {
  AccountForPropertyCorrectionFormQuery,
  Parcel,
  useAccountForPropertyCorrectionFormQuery,
} from "../../../../generated/graphql";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Button } from "../../../Common/Buttons/Button";
import { isMapCanvasTarget } from "../../../Maps/LayeredMap/utils";
import AccountBoundaryLayer from "../../../Maps/layers/accountBoundary";
import ParcelLayer from "../../../Maps/layers/parcels";
import { ZoomControl } from "../../../Maps/MapTools/ZoomControl";
import SimpleMap, { ViewportConfig } from "../../../Maps/SimpleMap";
import {
  onMapAvailable,
  useHighlightedParcel,
  useHoveredParcel,
} from "../../../Maps/utils/mapUtils";
import { OmniboxSearch } from "../../../Search/OmniboxSearch";
import { SearchContextProvider } from "../../../Search/SearchContext";
import {
  AddressErrors,
  AddressTooltip,
  CenterButton,
  DataRow,
  Divider,
  Label,
  LatLongInputRow,
  LocationWarnings,
  MapContainer,
  ParcelPaths,
  SearchBox,
  Value,
  ZoomControls,
} from "./__styles__/PropertyCorrection";
import { Form, InputRow, SectionTitle, Select, TextInput } from "./Common";
import pin from "./pin.svg";

type PropertyForm = {
  id: string;
  parcel: Maybe<{
    id: string;
    address: string;
    parcelId: string;
    lot: string;
    block: string;
    legalDescription: string;
  }>;
  longitude: number;
  latitude: number;
  streetAddress: string;
  city: string;
  state: string;
  zipcode: string;
  ignoreParcel: boolean;
};

const ParcelInfo = ({
  parcel,
}: {
  parcel: NonNullable<PropertyForm["parcel"]>;
}) => (
  <>
    <DataRow>
      <Label>Parcel ID:</Label>
      <Value>{parcel.parcelId}</Value>
    </DataRow>
    <DataRow>
      <Label>Lot Number:</Label>
      <Value>{parcel.lot}</Value>
    </DataRow>
    <DataRow>
      <Label>Block Number:</Label>
      <Value>{parcel.block}</Value>
    </DataRow>
    <DataRow>
      <Label>Legal Description:</Label>
      <Value>{parcel.legalDescription}</Value>
    </DataRow>
    <Divider />
  </>
);

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface MaybeCoordinates {
  latitude?: Maybe<number>;
  longitude?: Maybe<number>;
}

interface AddressTooltip {
  x?: number;
  y?: number;
  lat?: number;
  lng?: number;
  address: string;
}

export interface PropertyCorrectionsProps {
  get: <T extends keyof PropertyForm>(key: T) => PropertyForm[T];
  set: <T extends keyof PropertyForm>(
    key: T,
  ) => (value: PropertyForm[T]) => void;
  errors?: Maybe<Array<{ field: string; message: string }>>;
  account: NonNullable<AccountForPropertyCorrectionFormQuery["account"]>;
  setLoadingLocation: Dispatch<SetStateAction<boolean>>;
}

export function PropertyCorrections({
  get,
  set,
  errors,
  account,
  setLoadingLocation,
}: PropertyCorrectionsProps) {
  const _mapRef = useRef<MapRef | null>(null);
  const [addressTooltip, setAddressTooltip] = useState<Maybe<AddressTooltip>>();
  const initialParcels: Array<NonNullable<PropertyForm["parcel"]>> =
    get("parcel")?.id ? [get("parcel")!] : [];
  const [parcels, setParcels] = useState(initialParcels);
  const [inputLatitude, setInputLatitude] = useState(get("latitude"));
  const [inputLongitude, setInputLongitude] = useState(get("longitude"));

  const { removeParcelHighlight, getHighlightedParcel, highlightParcelById } =
    useHighlightedParcel();

  const { hoverOverParcel, removeParcelHover } = useHoveredParcel({
    mapRef: _mapRef,
  });

  const { account: authAccount } = useContext(AuthContext);

  const mViewport = useMemo(
    () =>
      new WebMercatorViewport({
        width: window.innerWidth / 2,
        height: 500,
      }),
    [],
  );

  const [viewport, setViewport] = useState<ViewportConfig>({
    ...mViewport.fitBounds(
      account.bounds as [[number, number], [number, number]],
    ),
  });

  const validLatLong = confirmValidCoordinates({
    latitude: get("latitude"),
    longitude: get("longitude"),
  });

  const centerMap = useCallback(({ latitude, longitude }: Coordinates) => {
    if (confirmValidCoordinates({ latitude, longitude })) {
      const map = _mapRef.current?.getMap();
      if (map) {
        map.flyTo({
          center: [longitude, latitude],
          zoom: 16,
          duration: 500,
        });
      }
    }
  }, []);

  const watchedParcelId = get("parcel")?.id ?? null;

  useEffect(() => {
    if (watchedParcelId) {
      set("ignoreParcel")(false);
    } else {
      set("ignoreParcel")(true);
    }
  }, [watchedParcelId, set]);

  const watchedLatitude = get("latitude");
  const watchedLongitude = get("longitude");

  useEffect(() => {
    setInputLatitude(watchedLatitude);
    setInputLongitude(watchedLongitude);
  }, [watchedLatitude, watchedLongitude]);

  const setParcel = useCallback(
    async (parcel: PropertyForm["parcel"]) => {
      set("parcel")(parcel);

      if (parcel?.id) {
        await onMapAvailable(_mapRef, map =>
          highlightParcelById(map, parcel.id),
        );
      } else {
        await onMapAvailable(_mapRef, removeParcelHighlight);
      }
    },
    [_mapRef, set, removeParcelHighlight, highlightParcelById],
  );

  type ParcelAtPoint = NonNullable<PropertyForm["parcel"]>;

  const getParcelsAtPoint = useCallback(
    async ({ latitude, longitude }: MaybeCoordinates) => {
      const transformMapboxParcel = ({
        properties,
      }: {
        properties: Parcel & { parcelNumber: string };
      }): ParcelAtPoint => {
        return {
          id: properties.id,
          address: properties.address ?? "",
          parcelId: properties.parcelNumber,
          lot: properties.lot ?? "",
          block: properties.block ?? "",
          legalDescription: properties.legalDescription ?? "",
        };
      };

      return onMapAvailable<Array<ParcelAtPoint>>(_mapRef, (map: any) => {
        const point = map.project([longitude, latitude]);
        const parcels = map.queryRenderedFeatures(point, {
          layers: ["parcels"],
        });

        return _.uniqBy(parcels.map(transformMapboxParcel), p => p.id);
      });
    },
    [],
  );

  const setParcelPathFromPoint = useCallback(
    async ({ latitude, longitude }: MaybeCoordinates) => {
      const parcelsAtPoint = await getParcelsAtPoint({ latitude, longitude });

      if (!parcelsAtPoint?.length) {
        await setParcel(null);
        setParcels([]);
      } else if (parcelsAtPoint.length === 1) {
        await setParcel(parcelsAtPoint[0] ?? null);
        setParcels(parcelsAtPoint);
      } else {
        await setParcel(null);
        setParcels(parcelsAtPoint);
      }

      setLoadingLocation(false);
    },
    [getParcelsAtPoint, setParcel, setParcels, setLoadingLocation],
  );

  const _handleMapClick = async (evt: MapLayerMouseEvent) => {
    const target = evt.originalEvent.target as HTMLElement;
    if (!isMapCanvasTarget(target)) return;
    setLoadingLocation(true);

    const { lng: longitude, lat: latitude } = evt.lngLat;

    set("latitude")(Number(latitude));
    set("longitude")(Number(longitude));

    await setParcelPathFromPoint({ latitude, longitude });
  };

  const _assignLatitudeLongitude = useCallback(
    async ({ longitude, latitude }: Coordinates) => {
      set("latitude")(Number(latitude));
      set("longitude")(Number(longitude));

      const oldLatitude = viewport.latitude;
      const oldLongitude = viewport.longitude;
      const differentDegrees = (a: number, b: number) =>
        Math.abs(a - b) > 0.000001;

      // if we're moving an infinitely small amount, don't bother panning around
      // this prevents jitter if you're searching for an address literally next
      // to the currently highlighted parcel
      if (
        differentDegrees(oldLatitude, latitude) ||
        differentDegrees(oldLongitude, longitude)
      ) {
        centerMap({ latitude, longitude });

        // if you're searching for an address and parcels
        // have not yet been loaded because of your zoom level
        // , we have to wait until the map shows the parcel
        // layer before highlighting those parcels
        const map = _mapRef.current?.getMap();
        if (map) {
          map.once("idle", async () => {
            await setParcelPathFromPoint({ latitude, longitude });
          });
        }
      } else {
        await setParcelPathFromPoint({ latitude, longitude });
      }
    },
    [
      centerMap,
      setParcelPathFromPoint,
      set,
      viewport.latitude,
      viewport.longitude,
    ],
  );

  const _handleSearchResult = useCallback(
    async (data: SearchResultProps) => {
      if (isCustomMapGeometryResult(data)) return;
      setLoadingLocation(true);
      const { longitude, latitude } = data.point;
      await _assignLatitudeLongitude({ longitude, latitude });
    },
    [setLoadingLocation, _assignLatitudeLongitude],
  );

  const _handleMapHover = async (evt: MapLayerMouseEvent) => {
    await onMapAvailable(_mapRef, () => {
      removeParcelHover();

      const feature = (evt.features || []).find(f => f.layer.id === "parcels");

      if (feature?.id) {
        hoverOverParcel(String(feature.id));

        setAddressTooltip({
          x: evt.point.x,
          y: evt.point.y,
          lng: evt.lngLat.lng,
          lat: evt.lngLat.lat,
          address: feature.properties?.address,
        });
      } else {
        setAddressTooltip(null);
      }
    });
  };

  const locationWarnings =
    !get("parcel") ?
      "A null parcel is dangerous! Please make absolutely sure that the selected property really does not fall within a parcel"
    : "None";

  const loadComplete = async () => {
    if (!_mapRef?.current?.getMap) {
      return;
    }

    if (validLatLong) {
      centerMap({ latitude: get("latitude"), longitude: get("longitude") });
      setInputLatitude(get("latitude"));
      setInputLongitude(get("longitude"));
    }

    // on initial map load, if you have a parcel on the property,
    // we should make sure to highlight that parcel if you zoom in
    // to the point where the parcel layer renders

    await onMapAvailable(_mapRef, (map: any) => {
      map.on("sourcedata", ({ sourceId }: { sourceId: string }) => {
        const parcel = get("parcel");

        if (sourceId === "parcels" && !getHighlightedParcel() && parcel?.id) {
          highlightParcelById(map, parcel.id);
        }
      });
    });
  };

  // nothing that instantiates this component ever puts a non-null value for errors
  const errorsByField = _.groupBy(errors, error => error.field);
  const errorForField = (field: keyof typeof errorsByField) =>
    (errorsByField[field] || []).map(error => error.message).join(",");

  const parcelError =
    parcels.length > 1 && !get("parcel")?.id ?
      "Multiple parcels detected"
    : errorForField("parcel");

  const addressError = errorForField("address");

  const coordinatesError =
    !validLatLong && (inputLatitude || inputLongitude) ?
      "Invalid latitude or longitude"
    : errorForField("coordinates");

  const parcel = get("parcel");

  return (
    <Form>
      {parcel && <ParcelInfo parcel={parcel} />}
      <SectionTitle>Property Address</SectionTitle>

      <TextInput
        name="streetAddress"
        value={get("streetAddress")}
        onChange={set("streetAddress")}
        label="Address"
      />

      <InputRow>
        <TextInput
          name="city"
          value={get("city")}
          onChange={set("city")}
          label="City"
          error={errorForField("city")}
        />

        <TextInput
          name="state"
          value={get("state")}
          onChange={set("state")}
          label="State"
        />

        <TextInput
          name="zipcode"
          value={get("zipcode")}
          onChange={set("zipcode")}
          label="Zipcode"
        />
      </InputRow>

      {addressError && <AddressErrors>{addressError}</AddressErrors>}

      <Divider />

      <SectionTitle>Property Location</SectionTitle>

      <LatLongInputRow>
        <InputRow>
          <TextInput
            name="latitude"
            value={inputLatitude}
            onChange={setInputLatitude}
            onBlur={async ({
              target: { value: input },
            }: FocusEvent<HTMLInputElement>) => {
              const latitude = Number(input);
              await _assignLatitudeLongitude({
                latitude,
                longitude: viewport.longitude,
              });
            }}
            label="Latitude"
            error={coordinatesError}
          />

          <TextInput
            name="longitude"
            value={inputLongitude}
            onChange={setInputLongitude}
            onBlur={async ({
              target: { value: input },
            }: FocusEvent<HTMLInputElement>) => {
              const longitude = Number(input);
              await _assignLatitudeLongitude({
                longitude,
                latitude: viewport.latitude,
              });
            }}
            label="Longitude"
          />
        </InputRow>
      </LatLongInputRow>

      <ParcelPaths>
        <Select
          label="Address"
          value={get("parcel")?.id}
          name="parcel"
          onChange={(id: string) =>
            setParcel(parcels.find(parcel => parcel.id === id)!)
          }
          disabled={parcels.length <= 1}
          options={parcels.map(parcel => {
            return {
              value: parcel.id,
              label: parcel.address,
            };
          })}
          error={parcelError}
        />
      </ParcelPaths>

      <MapContainer>
        <SearchBox>
          <SearchContextProvider
            handleSearchResultClick={_handleSearchResult}
            // If we already have an account in auth context, we don't need to pass the accountId
            accountId={authAccount ? undefined : account.id}
          >
            <OmniboxSearch compactSearchPanel />
          </SearchContextProvider>
        </SearchBox>
        <SimpleMap
          ref={_mapRef}
          {...viewport}
          width="100%"
          height="500px"
          interactiveLayerIds={["parcels"]}
          onClick={_handleMapClick}
          onMouseMove={_handleMapHover}
          setViewport={setViewport}
          cursor="pointer"
          baseMapStyle={account.defaultBaseMap.mapboxStyle}
          onLoad={loadComplete}
        >
          {addressTooltip && (
            <AddressTooltip
              css={{ top: addressTooltip.y, left: addressTooltip.x }}
            >
              {addressTooltip.address}
            </AddressTooltip>
          )}

          <AccountBoundaryLayer accountId={account.id} />
          <ParcelLayer accountId={account.id} />
          {validLatLong && (
            <Marker latitude={get("latitude")} longitude={get("longitude")}>
              <img src={pin} width={"10px"} height={"10px"} />
            </Marker>
          )}

          {validLatLong && (
            <CenterButton>
              <Button
                size="medium"
                variant="primary"
                onPress={() => {
                  centerMap({
                    longitude: get("longitude"),
                    latitude: get("latitude"),
                  });
                }}
                title="Center Map On Coordinates"
              />
            </CenterButton>
          )}
          <ZoomControls>
            <ZoomControl />
          </ZoomControls>
        </SimpleMap>
      </MapContainer>

      <LocationWarnings>Location Warnings: {locationWarnings}</LocationWarnings>
    </Form>
  );
}

export default (
  args: {
    accountId: string;
    setProcessing: Dispatch<SetStateAction<boolean>>;
  } & Omit<PropertyCorrectionsProps, "account" | "setLoadingLocation">,
) => {
  const { accountId } = args;
  const { loading, error, data } = useAccountForPropertyCorrectionFormQuery({
    variables: { id: accountId },
  });

  if (loading || error || !data?.account?.bounds) {
    return <div />;
  }

  return (
    <PropertyCorrections
      account={data.account}
      {...args}
      setLoadingLocation={args.setProcessing}
    />
  );
};
